import React from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import "./constants/global.css";
import "shepherd.js/dist/css/shepherd.css";
import "./tour/tourTheme.css";
import App from "./app/App";
import reportWebVitals from "./reportWebVitals";

import FontStyles from "./assets/fonts/fonts";

// Sentry error monitoring (must init before React renders)
import { initSentry } from "./sentry";
// MUI X License
import { LicenseInfo } from "@mui/x-license";

initSentry();
LicenseInfo.setLicenseKey(import.meta.env.VITE_MUI_X_LICENSE_KEY);

// Handle chunk loading errors by reloading the page
// This happens when a user has an old version of the app during a deployment.
// Guarded: in dev a compile error produces the same signature and an
// unconditional reload turns it into an unreadable crash loop; and even in
// prod we cap at 2 attempts so a genuinely broken deploy doesn't loop forever.
const CHUNK_RELOAD_KEY = "t3ChunkErrorReloads";
const reloadForChunkError = (context) => {
  if (!import.meta.env.PROD) {
    console.error(
      `Chunk load error (${context}) in dev — NOT auto-reloading. ` +
        "Check the Vite terminal/console for the real compile error.",
    );
    return;
  }
  const count = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) || 0);
  if (count >= 2) {
    console.error("Chunk load error persists after 2 reloads — giving up.");
    return;
  }
  sessionStorage.setItem(CHUNK_RELOAD_KEY, String(count + 1));
  window.location.reload();
};
// A load that stays healthy for 30s clears the counter
window.setTimeout(() => sessionStorage.removeItem(CHUNK_RELOAD_KEY), 30000);

window.addEventListener("error", (event) => {
  // Check if this is a chunk loading error
  if (
    event.message?.includes("Loading chunk") ||
    event.message?.includes("ChunkLoadError") ||
    event.message?.includes("Failed to fetch dynamically imported module") ||
    event.message?.includes("is not a valid JavaScript MIME type") ||
    (event.filename?.includes(".chunk.js") && event.message?.includes("Unexpected token"))
  ) {
    console.warn("Chunk load error detected");
    reloadForChunkError("error event");
  }
});

// Also handle unhandled promise rejections for chunk loading errors
window.addEventListener("unhandledrejection", (event) => {
  if (
    event.reason?.message?.includes("Loading chunk") ||
    event.reason?.message?.includes("Failed to fetch dynamically imported module") ||
    event.reason?.message?.includes("is not a valid JavaScript MIME type") ||
    event.reason?.name === "ChunkLoadError"
  ) {
    console.warn("Chunk load error detected in promise");
    reloadForChunkError("unhandled rejection");
  }
});

// React 18 createRoot API
const container = document.getElementById("root");
const root = createRoot(container);
root.render(
  <React.Fragment>
    <FontStyles />
    <App />
  </React.Fragment>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
